// Pooled projectiles with custom motion and swept collision.
//
// Every projectile is three pooled images: an additive halo, a stretched additive streak
// (trail) and the core sprite. Motion is integrated here (velocity, gravity, homing, launch
// ramp); collision sweeps the frame's movement segment against
//   - world solids (sampled every <= 12 px; one-way platforms let shots through),
//   - enemy hit areas (slab test, areas expanded by the shot radius; weak-point multipliers),
//     frontal shields (enemy.blocks), destructible enemy shots,
//   - the player body rect (enemy shots; dashing through i-frames lets shots pass).
// Hits go through ctx.damage; impacts through fx. Built for 400+ live shots at 60 fps:
// no allocation per projectile per frame on the hot path.

import Phaser from 'phaser';
import { TEX } from '../../art/keys';
import { DEPTH } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type {
  EnemyApi,
  FxApi,
  GameContext,
  HitArea,
  ProjectileHandle,
  ProjectileSpec,
  ProjectileZone,
} from '../../core/contracts';
import type { Tag } from '../../core/types';
import { segmentVsAabb, segmentVsCircle } from './geom';
import { destroyObj, enemyList, livePlayer, playerStats, withTag, type DamageInfoEx, type ImpactKind, type ProjectileSpecEx, type ProjectileSystem } from './internal';
import { rangeFalloff } from './math';
import type { CombatSfx } from './sfx';
import { CTEX, ensureCombatTextures } from './textures';

const INITIAL_POOL = 96;
const MAX_PROJECTILES = 1400;
/** World-solid sampling step along the movement segment. */
const SOLID_STEP = 12;
/** Homing target re-acquisition interval. */
const RETARGET = 0.12;
const OUT_OF_BOUNDS = 240;
const ADD = Phaser.BlendModes.ADD;
const NORMAL = Phaser.BlendModes.NORMAL;

type TrailKind = NonNullable<ProjectileSpec['trail']>;

/** Trail look per kind: max streak length (px), thickness (× radius), alpha. */
const TRAIL_LOOK: Record<TrailKind, { len: number; thick: number; alpha: number }> = {
  pulse: { len: 74, thick: 1.5, alpha: 0.75 },
  rocket: { len: 46, thick: 1.6, alpha: 0.8 },
  rail: { len: 560, thick: 1.3, alpha: 0.95 },
  enemy: { len: 46, thick: 1.6, alpha: 0.7 },
  gravity: { len: 36, thick: 1.4, alpha: 0.6 },
  none: { len: 0, thick: 0, alpha: 0 },
};

/** Generated fallback core texture when a shared projectile texture is missing. */
function fallbackCore(key: string): string {
  switch (key) {
    case TEX.projRail:
    case TEX.projEnemyNeedle:
      return CTEX.rail;
    case TEX.projRocket:
      return CTEX.rocket;
    case TEX.projGravity:
    case TEX.projEnemyOrb:
    case TEX.projMortar:
    case TEX.projPellet:
      return CTEX.orb;
    default:
      return CTEX.bolt;
  }
}

/** Collision candidate (reused). */
interface Cand {
  e: EnemyApi;
  t: number;
  mult: number;
}

/** Per-frame cache of a targetable enemy and its hit areas. */
interface EnemyEntry {
  e: EnemyApi;
  areas: readonly HitArea[];
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

class Proj implements ProjectileHandle {
  spec: ProjectileSpecEx = null as unknown as ProjectileSpecEx;
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  damage = 0;
  scale = 1;
  alive = false;
  readonly touchedZones = new Set<number>();
  readonly hits = new Set<EnemyApi>();
  age = 0;
  lifetime = 1;
  travelled = 0;
  pierceLeft = 0;
  bouncesLeft = 0;
  ox = 0;
  oy = 0;
  speed = 0;
  fresh = true;
  homeTarget: EnemyApi | null = null;
  homeT = 0;
  puffT = 0;
  trailLen = 0;
  trailThick = 0;
  trailAlpha = 0;
  glowAlpha = 0.5;
  glowSize = 24;
  coreAlpha = 1;
  color = 0xffffff;
  /** Set by kill(): visuals hidden, returned to the pool at the end of the update. */
  pendingFree = false;

  constructor(
    readonly sys: { kill(p: Proj, explode: boolean): void },
    readonly img: Phaser.GameObjects.Image,
    readonly glow: Phaser.GameObjects.Image,
    readonly trail: Phaser.GameObjects.Image,
  ) {}

  kill(explode?: boolean): void {
    if (!this.alive) return;
    this.sys.kill(this, explode ?? false);
  }
}

export function createProjectiles(ctx: GameContext, fx: FxApi, sfx: CombatSfx): ProjectileSystem {
  const scene = ctx.scene;
  const pool: Proj[] = [];
  const active: Proj[] = [];
  const zones: { id: number; zone: ProjectileZone }[] = [];
  let nextZoneId = 1;
  let aliveCount = 0;
  let destroyed = false;
  let texturesReady = false;

  // Per-frame caches (reused arrays).
  const enemyCache: EnemyEntry[] = [];
  let enemyCacheLen = 0;
  const destructibles: Proj[] = [];
  const cands: Cand[] = [];
  let candLen = 0;

  const sysRef = {
    kill: (p: Proj, explode: boolean) => killProj(p, explode),
  };

  function makeProj(): Proj {
    if (!texturesReady) {
      ensureCombatTextures(scene);
      texturesReady = true;
    }
    const trail = scene.add.image(0, 0, CTEX.streak).setOrigin(1, 0.5).setBlendMode(ADD).setDepth(DEPTH.PROJECTILES - 2);
    const glow = scene.add.image(0, 0, CTEX.soft).setBlendMode(ADD).setDepth(DEPTH.PROJECTILES - 1);
    const img = scene.add.image(0, 0, CTEX.bolt).setDepth(DEPTH.PROJECTILES);
    for (const o of [trail, glow, img]) o.setVisible(false).setActive(false);
    return new Proj(sysRef, img, glow, trail);
  }

  function hide(p: Proj): void {
    p.img.setVisible(false).setActive(false);
    p.glow.setVisible(false).setActive(false);
    p.trail.setVisible(false).setActive(false);
  }

  function setVisuals(p: Proj): void {
    const s = p.spec;
    const enemy = s.owner === 'enemy' && !s.ghost;
    const real = !!s.texture && scene.textures.exists(s.texture);
    const key = real ? s.texture : fallbackCore(s.texture);
    const img = p.img;
    if (real && s.frame !== undefined) img.setTexture(key, s.frame);
    else img.setTexture(key);
    const baseColor = s.tint ?? (enemy ? COLORS.threat : COLORS.voltGlow);
    p.color = baseColor;
    if (s.tint !== undefined || !real) img.setTint(baseColor);
    else img.clearTint();
    img.setBlendMode(s.glow ? ADD : NORMAL);
    // Enemy shots are drawn a bit larger than their hitbox so threats read over busy fights.
    p.scale = (s.scale ?? 1) * (enemy ? 1.18 : 1);
    img.setScale(p.scale).setAlpha(1).setVisible(true).setActive(true);
    p.coreAlpha = 1;

    const r = Math.max(2, s.radius);
    p.glowSize = r * (enemy ? 7.5 : 5.5) * Math.max(0.6, s.scale ?? 1);
    p.glowAlpha = enemy ? 0.7 : s.trail === 'rail' ? 0.75 : 0.45;
    p.glow.setTexture(scene.textures.exists(TEX.soft) ? TEX.soft : CTEX.soft);
    p.glow.setTint(baseColor).setDisplaySize(p.glowSize, p.glowSize).setAlpha(p.glowAlpha).setVisible(true).setActive(true);

    const kind: TrailKind = s.trail ?? (enemy ? 'enemy' : 'pulse');
    const look = TRAIL_LOOK[kind] ?? TRAIL_LOOK.pulse;
    p.trailLen = look.len * (kind === 'pulse' ? Math.min(1.4, Math.max(0.5, s.speed / 1800)) : 1);
    p.trailThick = Math.max(3, r * 2 * look.thick);
    p.trailAlpha = look.alpha;
    if (p.trailLen > 0) {
      p.trail.setTint(kind === 'rocket' ? 0xffb35a : baseColor).setAlpha(0).setVisible(true).setActive(true);
      p.trail.setDisplaySize(1, p.trailThick);
    } else p.trail.setVisible(false).setActive(false);
  }

  function spawn(spec: ProjectileSpec): ProjectileHandle {
    const s = spec as ProjectileSpecEx;
    let p: Proj | undefined;
    if (destroyed) {
      // Room is gone: hand back an inert handle.
      p = new Proj(sysRef, null as unknown as Phaser.GameObjects.Image, null as unknown as Phaser.GameObjects.Image, null as unknown as Phaser.GameObjects.Image);
      p.spec = s;
      return p;
    }
    p = pool.pop();
    if (!p) {
      if (active.length >= MAX_PROJECTILES) {
        // Saturated: recycle the oldest live shot quietly.
        const oldest = active.find((q) => q.alive && !q.pendingFree);
        if (oldest) killProj(oldest, false, true);
        p = makeProj();
      } else p = makeProj();
    }
    p.spec = s;
    p.alive = true;
    p.pendingFree = false;
    p.fresh = true;
    p.x = p.ox = s.x;
    p.y = p.oy = s.y;
    const sp = Math.max(0, s.speed) * (s.launchSpeedFrac ?? 1);
    p.speed = sp;
    p.vx = Math.cos(s.angle) * sp;
    p.vy = Math.sin(s.angle) * sp;
    p.damage = s.damage;
    p.age = 0;
    p.lifetime = Math.max(0.02, s.lifetime);
    p.travelled = 0;
    p.pierceLeft = Math.max(0, Math.round(s.pierce ?? 0));
    p.bouncesLeft = Math.max(0, Math.round(s.bounces ?? 0));
    p.homeTarget = null;
    p.homeT = 0;
    p.puffT = 0;
    p.hits.clear();
    p.touchedZones.clear();
    setVisuals(p);
    placeVisuals(p);
    active.push(p);
    aliveCount++;
    return p;
  }

  /** Remove a projectile, optionally detonating its payload (explosion / gravity field). */
  function killProj(p: Proj, explode: boolean, silent = false): void {
    if (!p.alive) return;
    p.alive = false;
    p.pendingFree = true;
    aliveCount--;
    const s = p.spec;
    if (!silent && !destroyed) {
      if (explode) detonate(p);
      if (s.trail === 'rail') {
        fx.beam(p.ox, p.oy, p.x, p.y, { color: p.color, width: 5, duration: 240 });
      }
    }
    if (p.img) hide(p);
  }

  function detonate(p: Proj): void {
    const s = p.spec;
    if (s.ghost) {
      // Visual blast + the same terrain chipping the real one causes on the shooter's side.
      const r = s.explode?.radius ?? s.field?.radius ?? 0;
      if (r > 0) {
        try {
          fx.explosion(p.x, p.y, r, s.field ? COLORS.gravity : (s.tint ?? COLORS.explosive));
          if (s.explode) ctx.world.damageTerrain(p.x, p.y, s.explode.damage, r);
        } catch {
          // Cosmetic only.
        }
      }
      return;
    }
    const player = s.owner === 'player';
    const stats = player ? playerStats(ctx) : null;
    try {
      if (s.explode) {
        const radius = s.explode.radius * (stats ? stats.explosionRadius : 1);
        const tags: Tag[] = withTag(s.explode.tags ?? s.tags, 'explosive');
        ctx.damage.explode(
          p.x,
          p.y,
          radius,
          {
            amount: s.explode.damage,
            tags,
            source: s.owner,
            weaponId: s.weaponId,
            knockback: Math.max(180, s.knockback ?? 0),
            secondary: s.secondary,
            noTrigger: s.noTrigger,
            label: s.label ?? 'blast',
          } as DamageInfoEx,
          { color: player ? (s.tint ?? COLORS.explosive) : COLORS.threatHot, hurtsPlayer: !player },
        );
      }
      if (s.field) {
        ctx.zones.add({
          kind: 'gravity',
          owner: s.owner,
          x: p.x,
          y: p.y,
          radius: s.field.radius,
          duration: s.field.duration,
          strength: s.field.strength,
          endDamage: s.field.implodeDamage,
          tags: ['gravity'],
          color: COLORS.gravity,
        });
      }
    } catch (err) {
      console.warn('[combat] projectile payload failed', err);
    }
  }

  function placeVisuals(p: Proj): void {
    const ang = Math.atan2(p.vy, p.vx);
    const s = p.spec;
    // Lifetime fade over the last 12% (min 0.08 s), so range-limited shots dissolve instead of popping.
    const fadeWin = Math.max(0.08, p.lifetime * 0.12);
    const left = p.lifetime - p.age;
    const fade = left < fadeWin ? Math.max(0, left / fadeWin) : 1;
    p.img.setPosition(p.x, p.y).setRotation(ang).setAlpha(fade);
    let ga = p.glowAlpha * fade;
    if (s.owner === 'enemy' && !s.ghost) ga *= 0.82 + 0.18 * Math.sin(p.age * 18 + p.ox);
    p.glow.setPosition(p.x, p.y).setAlpha(ga);
    if (p.trailLen > 0) {
      const len = Math.min(p.trailLen, p.travelled + 2);
      p.trail.setPosition(p.x, p.y).setRotation(ang).setDisplaySize(Math.max(1, len), p.trailThick).setAlpha(p.trailAlpha * fade);
    }
  }

  // ───────────── collision helpers ─────────────

  function buildFrameCaches(): void {
    enemyCacheLen = 0;
    for (const e of enemyList(ctx)) {
      if (!e.alive || e.spawning) continue;
      let areas: readonly HitArea[];
      try {
        areas = e.hitAreas();
      } catch {
        continue;
      }
      if (!areas || areas.length === 0) continue;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (let i = 0; i < areas.length; i++) {
        const a = areas[i];
        if (a.x < minX) minX = a.x;
        if (a.y < minY) minY = a.y;
        if (a.x + a.w > maxX) maxX = a.x + a.w;
        if (a.y + a.h > maxY) maxY = a.y + a.h;
      }
      let entry = enemyCache[enemyCacheLen];
      if (!entry) {
        entry = { e, areas, minX, minY, maxX, maxY };
        enemyCache[enemyCacheLen] = entry;
      } else {
        entry.e = e;
        entry.areas = areas;
        entry.minX = minX;
        entry.minY = minY;
        entry.maxX = maxX;
        entry.maxY = maxY;
      }
      enemyCacheLen++;
    }
    destructibles.length = 0;
    for (let i = 0; i < active.length; i++) {
      const q = active[i];
      if (q.alive && q.spec.owner === 'enemy' && q.spec.destructible) destructibles.push(q);
    }
  }

  function solidAt(x: number, y: number): boolean {
    try {
      return ctx.world.isSolidAt(x, y) && !ctx.world.isOneWayAt(x, y);
    } catch {
      return false;
    }
  }

  /** First sampled t in (0,1] where the segment is inside a solid, or 2 when clear. */
  function worldHitT(x0: number, y0: number, dx: number, dy: number, len: number): number {
    const n = Math.max(1, Math.ceil(len / SOLID_STEP));
    for (let i = 1; i <= n; i++) {
      const t = i / n;
      if (solidAt(x0 + dx * t, y0 + dy * t)) return t;
    }
    return 2;
  }

  function pushCand(e: EnemyApi, t: number, mult: number): void {
    let c = cands[candLen];
    if (!c) {
      c = { e, t, mult };
      cands[candLen] = c;
    } else {
      c.e = e;
      c.t = t;
      c.mult = mult;
    }
    candLen++;
  }

  /** Enemies the segment touches before tMax, sorted by entry t (insertion sort: lists are tiny). */
  function gatherEnemyHits(p: Proj, x0: number, y0: number, dx: number, dy: number, tMax: number): void {
    candLen = 0;
    const r = Math.max(1, p.spec.radius);
    const sx0 = Math.min(x0, x0 + dx) - r;
    const sx1 = Math.max(x0, x0 + dx) + r;
    const sy0 = Math.min(y0, y0 + dy) - r;
    const sy1 = Math.max(y0, y0 + dy) + r;
    for (let i = 0; i < enemyCacheLen; i++) {
      const en = enemyCache[i];
      if (en.maxX < sx0 || en.minX > sx1 || en.maxY < sy0 || en.minY > sy1) continue;
      if (p.hits.has(en.e) || !en.e.alive) continue;
      let bestT = 2;
      let bestMult = 1;
      for (let k = 0; k < en.areas.length; k++) {
        const a = en.areas[k];
        const t = segmentVsAabb(x0, y0, dx, dy, a.x - r, a.y - r, a.x + a.w + r, a.y + a.h + r);
        if (t < 0) continue;
        // Prefer the earliest area; on (near) ties prefer the weak point.
        if (t < bestT - 1e-6 || (Math.abs(t - bestT) <= 1e-6 && a.mult > bestMult)) {
          bestT = t;
          bestMult = a.mult;
        }
      }
      if (bestT <= tMax) pushCand(en.e, bestT, bestMult);
    }
    for (let i = 1; i < candLen; i++) {
      const c = cands[i];
      const ce = c.e;
      const ct = c.t;
      const cm = c.mult;
      let j = i - 1;
      while (j >= 0 && cands[j].t > ct) {
        const a = cands[j + 1];
        const b = cands[j];
        a.e = b.e;
        a.t = b.t;
        a.mult = b.mult;
        j--;
      }
      const d = cands[j + 1];
      d.e = ce;
      d.t = ct;
      d.mult = cm;
    }
  }

  function impactKind(s: ProjectileSpecEx): ImpactKind {
    if (s.impact) return s.impact;
    if (s.texture === TEX.projPellet) return 'pellet';
    if (s.trail === 'rail' || s.radius >= 8) return 'heavy';
    if (s.glow || s.tags.includes('electric')) return 'energy';
    return 'bullet';
  }

  /** Player shot vs enemies along the segment. Returns true if the projectile died. */
  function hitEnemies(p: Proj, x0: number, y0: number, dx: number, dy: number, tMax: number, segLen: number): boolean {
    gatherEnemyHits(p, x0, y0, dx, dy, tMax);
    if (candLen === 0) return false;
    const s = p.spec;
    const ang = Math.atan2(dy, dx);
    for (let i = 0; i < candLen; i++) {
      const c = cands[i];
      const e = c.e;
      if (!e.alive || p.hits.has(e)) continue;
      const hx = x0 + dx * c.t;
      const hy = y0 + dy * c.t;
      p.hits.add(e);
      let blocked = false;
      try {
        blocked = !!e.blocks?.(x0, y0);
      } catch {
        blocked = false;
      }
      if (blocked) {
        p.x = hx;
        p.y = hy;
        fx.impact(hx, hy, ang + Math.PI, 'shield', COLORS.shield);
        sfx.play('hit_shield', { volume: 0.55, x: hx }, 45);
        if (s.explode) {
          // The blast still goes off at the shield face, but the shield bearer is spared.
          const radius = s.explode.radius * playerStats(ctx).explosionRadius;
          try {
            ctx.damage.explode(
              hx - Math.cos(ang) * 6,
              hy - Math.sin(ang) * 6,
              radius,
              { amount: s.explode.damage, tags: withTag(s.explode.tags ?? s.tags, 'explosive'), source: 'player', weaponId: s.weaponId, knockback: 200, secondary: s.secondary, noTrigger: s.noTrigger },
              { color: s.tint ?? COLORS.explosive, exclude: new Set([e]) },
            );
          } catch (err) {
            console.warn('[combat] shield blast failed', err);
          }
        }
        killProj(p, false);
        return true;
      }
      const travelled = p.travelled + segLen * c.t;
      const amount = p.damage * rangeFalloff(travelled, s.falloff) * (c.mult > 0 ? c.mult : 1);
      const info: DamageInfoEx = {
        amount,
        tags: s.tags,
        source: 'player',
        sourceX: x0,
        sourceY: y0,
        knockback: s.knockback ? s.knockback * rangeFalloff(travelled, s.falloff) : undefined,
        kbAngle: ang,
        weaponId: s.weaponId,
        crit: s.crit,
        statuses: s.statuses,
        secondary: s.secondary,
        noTrigger: s.noTrigger,
        label: s.label,
        explodeOnKill: s.explodeOnKill,
      };
      try {
        ctx.damage.hitEnemy(e, info);
      } catch (err) {
        console.warn('[combat] hitEnemy failed', err);
      }
      fx.impact(hx, hy, ang + Math.PI, impactKind(s), s.tint ?? p.color);
      if (s.chain && s.chain.count > 0) {
        try {
          ctx.damage.chainLightning({ x: e.x, y: e.y }, null, {
            count: Math.max(0, s.chain.count - 1),
            range: s.chain.range,
            damage: amount * s.chain.damageMult,
            exclude: new Set([e]),
            secondary: s.secondary,
          });
        } catch (err) {
          console.warn('[combat] projectile chain failed', err);
        }
      }
      if (s.explode || s.field) {
        p.x = hx;
        p.y = hy;
        killProj(p, true);
        return true;
      }
      p.pierceLeft--;
      if (p.pierceLeft < 0) {
        p.x = hx;
        p.y = hy;
        killProj(p, false);
        return true;
      }
    }
    return false;
  }

  /** Player shot vs destructible enemy shots. */
  function hitDestructibles(p: Proj, x0: number, y0: number, dx: number, dy: number, tMax: number): boolean {
    if (destructibles.length === 0) return false;
    const r = p.spec.radius;
    for (let i = 0; i < destructibles.length; i++) {
      const q = destructibles[i];
      if (!q.alive) continue;
      const t = segmentVsCircle(x0, y0, dx, dy, q.x, q.y, q.spec.radius + r);
      if (t < 0 || t > tMax) continue;
      fx.impact(q.x, q.y, Math.atan2(dy, dx) + Math.PI, 'energy', COLORS.threatGlow);
      killProj(q, false);
      p.pierceLeft--;
      if (p.pierceLeft < 0) {
        p.x = x0 + dx * t;
        p.y = y0 + dy * t;
        killProj(p, false);
        return true;
      }
    }
    return false;
  }

  /** Enemy shot vs the player body. Returns true if the projectile died. */
  function hitPlayer(p: Proj, x0: number, y0: number, dx: number, dy: number, tMax: number): boolean {
    const pl = livePlayer(ctx);
    if (!pl || !pl.alive) return false;
    const b = pl.body;
    if (!b) return false;
    const r = p.spec.radius * 0.8;
    const t = segmentVsAabb(x0, y0, dx, dy, b.x - r, b.y - r, b.x + b.width + r, b.y + b.height + r);
    if (t < 0 || t > tMax) return false;
    if (pl.invulnerable) return false; // dash / i-frames: the shot passes through
    const s = p.spec;
    const ang = Math.atan2(dy, dx);
    const hx = x0 + dx * t;
    const hy = y0 + dy * t;
    let res;
    try {
      res = ctx.damage.hitPlayer({
        amount: p.damage,
        tags: s.tags,
        source: 'enemy',
        sourceX: x0,
        sourceY: y0,
        knockback: s.knockback ?? 160,
        kbAngle: ang,
        label: s.label ?? 'shot',
      });
    } catch (err) {
      console.warn('[combat] hitPlayer failed', err);
      return false;
    }
    if (res.blocked && pl.invulnerable) return false;
    p.x = hx;
    p.y = hy;
    fx.impact(hx, hy, ang + Math.PI, s.explode ? 'heavy' : 'energy', p.color);
    killProj(p, !!(s.explode || s.field));
    return true;
  }

  /** Bounce off a wall: reflect the axis that is blocked. */
  function bounce(p: Proj, x0: number, y0: number, nx: number, ny: number): void {
    const hitX = solidAt(nx, y0);
    const hitY = solidAt(x0, ny);
    if (hitX || !hitY) p.vx = -p.vx * 0.8;
    if (hitY || !hitX) p.vy = -p.vy * 0.8;
    p.x = x0;
    p.y = y0;
    p.hits.clear();
  }

  function applyZones(p: Proj): void {
    for (let i = 0; i < zones.length; i++) {
      const { id, zone } = zones[i];
      if (p.touchedZones.has(id)) continue;
      if (zone.affects !== 'both' && zone.affects !== p.spec.owner) continue;
      const sh = zone.shape;
      let inside: boolean;
      if (sh.kind === 'circle') {
        const dx = p.x - sh.x;
        const dy = p.y - sh.y;
        inside = dx * dx + dy * dy <= sh.r * sh.r;
      } else inside = p.x >= sh.x && p.x <= sh.x + sh.w && p.y >= sh.y && p.y <= sh.y + sh.h;
      if (!inside) continue;
      p.touchedZones.add(id);
      try {
        zone.onEnter(p);
      } catch (err) {
        console.warn('[combat] projectile zone failed', err);
      }
      if (!p.alive) return;
    }
  }

  function steer(p: Proj, dt: number): void {
    const s = p.spec;
    const h = s.homing;
    if (!h || p.age < h.delay) return;
    p.homeT -= dt;
    if (p.homeT <= 0 || (p.homeTarget && !p.homeTarget.alive)) {
      p.homeT = RETARGET;
      if (s.owner === 'player') {
        try {
          p.homeTarget = ctx.enemies.nearest(p.x, p.y, h.range);
        } catch {
          p.homeTarget = null;
        }
      } else p.homeTarget = null;
    }
    let tx: number;
    let ty: number;
    if (s.owner === 'player') {
      const t = p.homeTarget;
      if (!t) return;
      tx = t.x;
      ty = t.y;
    } else {
      const pl = livePlayer(ctx);
      if (!pl || !pl.alive) return;
      tx = pl.x;
      ty = pl.y;
      const ddx = tx - p.x;
      const ddy = ty - p.y;
      if (ddx * ddx + ddy * ddy > h.range * h.range) return;
    }
    const cur = Math.atan2(p.vy, p.vx);
    const want = Math.atan2(ty - p.y, tx - p.x);
    let diff = want - cur;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    const maxTurn = h.turnRate * dt;
    const na = cur + Math.max(-maxTurn, Math.min(maxTurn, diff));
    const sp = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
    p.vx = Math.cos(na) * sp;
    p.vy = Math.sin(na) * sp;
  }

  function step(p: Proj, dt: number, worldW: number, worldH: number): void {
    const s = p.spec;
    p.age += dt;
    if (p.age >= p.lifetime) {
      killProj(p, !s.quietExpire && !!(s.explode || s.field));
      return;
    }
    // Micro-missile launch ramp: start slow out of the rack, then accelerate to full speed.
    if (s.launchSpeedFrac !== undefined && s.launchSpeedFrac < 1 && p.age < 0.3) {
      const k = s.launchSpeedFrac + (1 - s.launchSpeedFrac) * Math.min(1, p.age / 0.3);
      const target = s.speed * k;
      const cur = Math.sqrt(p.vx * p.vx + p.vy * p.vy) || 1;
      p.vx *= target / cur;
      p.vy *= target / cur;
    }
    steer(p, dt);
    if (s.gravity) p.vy += s.gravity * dt;

    const x0 = p.x;
    const y0 = p.y;
    const dx = p.vx * dt;
    const dy = p.vy * dt;
    const segLen = Math.sqrt(dx * dx + dy * dy);
    const tWorld = segLen > 0 ? worldHitT(x0, y0, dx, dy, segLen) : solidAt(x0, y0) ? 0 : 2;
    const tMax = Math.min(1, tWorld);

    if (s.ghost) {
      // Cosmetic mirror of a rival's shot: walls only (damage arrives over the network).
    } else if (s.owner === 'player') {
      // Point-blank: on the first step also test the stretch between the shoulder and the muzzle.
      if (p.fresh && s.backtrace && s.backtrace > 0 && segLen > 0) {
        const ux = Math.cos(s.angle);
        const uy = Math.sin(s.angle);
        const bx = x0 - ux * s.backtrace;
        const by = y0 - uy * s.backtrace;
        if (hitEnemies(p, bx, by, ux * s.backtrace, uy * s.backtrace, 1, s.backtrace)) return;
      }
      if (hitEnemies(p, x0, y0, dx, dy, tMax, segLen)) return;
      if (hitDestructibles(p, x0, y0, dx, dy, tMax)) return;
    } else if (hitPlayer(p, x0, y0, dx, dy, tMax)) return;
    p.fresh = false;

    if (tWorld <= 1) {
      // Impact point: the last sample outside the wall.
      const n = Math.max(1, Math.ceil(segLen / SOLID_STEP));
      const tb = Math.max(0, tWorld - 1 / n);
      const hx = x0 + dx * tb;
      const hy = y0 + dy * tb;
      // Player shots wear down terrain (explosive shots damage it through their explosion).
      if ((s.owner !== 'enemy' || s.ghost) && !s.explode && !s.field) {
        try {
          ctx.world.damageTerrain(x0 + dx * tWorld, y0 + dy * tWorld, p.damage);
        } catch {
          // World without destructible terrain.
        }
      }
      if (p.bouncesLeft > 0 && !s.explode && !s.field) {
        p.bouncesLeft--;
        bounce(p, hx, hy, x0 + dx * tWorld, y0 + dy * tWorld);
        fx.sparks(hx, hy, Math.atan2(-dy, -dx), { count: 3, color: p.color, speed: 220, spread: 1.2, life: 140 });
        return;
      }
      p.x = hx;
      p.y = hy;
      const ang = Math.atan2(dy, dx);
      if (!s.explode && !s.field) fx.impact(hx, hy, ang + Math.PI, s.owner === 'enemy' ? 'energy' : impactKind(s), p.color);
      killProj(p, true);
      return;
    }

    p.x = x0 + dx;
    p.y = y0 + dy;
    p.travelled += segLen;
    if (p.x < -OUT_OF_BOUNDS || p.y < -OUT_OF_BOUNDS * 2 || p.x > worldW + OUT_OF_BOUNDS || p.y > worldH + OUT_OF_BOUNDS) {
      killProj(p, false, true);
      return;
    }
    if (zones.length > 0) {
      applyZones(p);
      if (!p.alive) return;
    }
    // Rocket exhaust puffs.
    if (s.trail === 'rocket') {
      p.puffT -= dt;
      if (p.puffT <= 0) {
        p.puffT = 0.045;
        fx.smoke(p.x - p.vx * 0.012, p.y - p.vy * 0.012, { count: 1, color: 0x5a5560, scale: 0.32 });
      }
    }
    placeVisuals(p);
  }

  const api: ProjectileSystem = {
    spawn,

    addZone(zone) {
      const id = nextZoneId++;
      zones.push({ id, zone });
      return () => {
        const i = zones.findIndex((z) => z.id === id);
        if (i >= 0) zones.splice(i, 1);
      };
    },

    forEach(owner, fn) {
      for (let i = 0; i < active.length; i++) {
        const p = active[i];
        if (p.alive && p.spec.owner === owner) fn(p);
      }
    },

    clear(owner) {
      for (let i = 0; i < active.length; i++) {
        const p = active[i];
        if (!p.alive) continue;
        if (owner !== 'all' && p.spec.owner !== owner) continue;
        // Fizzle with a little spark so cleared bullets (room clear, ultimates) do not just vanish.
        fx.sparks(p.x, p.y, 0, { count: 2, color: p.color, speed: 120, spread: Math.PI * 2, life: 160 });
        killProj(p, false, true);
      }
    },

    get activeCount() {
      return aliveCount;
    },

    nudgeEnemyShots(x, y, radius, strength, dt) {
      const r2 = radius * radius;
      for (let i = 0; i < active.length; i++) {
        const p = active[i];
        if (!p.alive || p.spec.owner !== 'enemy') continue;
        const dx = x - p.x;
        const dy = y - p.y;
        const d2 = dx * dx + dy * dy;
        if (d2 > r2 || d2 < 1) continue;
        const d = Math.sqrt(d2);
        const k = (strength * (1 - d / radius) * dt) / d;
        p.vx += dx * k;
        p.vy += dy * k;
      }
    },

    start() {
      ensureCombatTextures(scene);
      texturesReady = true;
      // Warm the pool so the first big salvo does not allocate dozens of images mid-fight.
      while (pool.length < INITIAL_POOL) pool.push(makeProj());
    },

    update(dt) {
      if (destroyed || dt <= 0) return;
      if (active.length === 0) return;
      buildFrameCaches();
      let worldW = 1e6;
      let worldH = 1e6;
      try {
        worldW = ctx.world.width;
        worldH = ctx.world.height;
      } catch {
        // world unavailable (sandbox)
      }
      const n = active.length;
      for (let i = 0; i < n; i++) {
        const p = active[i];
        if (p.alive) step(p, dt, worldW, worldH);
      }
      // Compact: return dead projectiles to the pool (spawns during the loop were appended).
      let w = 0;
      for (let i = 0; i < active.length; i++) {
        const p = active[i];
        if (p.alive) active[w++] = p;
        else {
          p.pendingFree = false;
          pool.push(p);
        }
      }
      active.length = w;
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      for (const p of active) {
        p.alive = false;
        destroyObj(p.img);
        destroyObj(p.glow);
        destroyObj(p.trail);
      }
      for (const p of pool) {
        destroyObj(p.img);
        destroyObj(p.glow);
        destroyObj(p.trail);
      }
      active.length = 0;
      pool.length = 0;
      zones.length = 0;
      aliveCount = 0;
    },
  };
  return api;
}
